import { pilotDestinations } from './navigation';
import { getAttentionItems, getSetupTenants } from './homeModel';
import type { Tenant } from './homeModel';

export type WorkspaceFilter = 'all' | 'attention' | 'active' | 'setup';
export interface WorkspaceRequest { type: 'view'; view: 'tenant_list' | 'fleet_report'; title: string; filter?: WorkspaceFilter }
const destinations: Record<string, string> = { home: '/dashboard', tenants: '/tenants', showrooms: '/showrooms', releases: '/releases', mqtt: '/mqtt', ai: '/ai', insights: '/analytics', access: '/access-control' };

/** Model output is a request, never an executable URL or trusted authorization. */
export function resolveChatAction(action: unknown, hasPermission: (permission: string) => boolean):
  { kind: 'navigate'; path: string; label: string } | { kind: 'view'; view: WorkspaceRequest } | null {
  if (!action || typeof action !== 'object') return null;
  const candidate = action as Record<string, unknown>;
  if (candidate.type === 'navigate' && typeof candidate.destination === 'string') {
    const item = pilotDestinations.find(destination => destination.path === destinations[candidate.destination as string]);
    if (!item || (item.anyOf && !item.anyOf.some(hasPermission))) return null;
    return { kind: 'navigate', path: item.path, label: item.label };
  }
  if (candidate.type === 'view' && ['tenant_list', 'fleet_report'].includes(String(candidate.view))
    && (hasPermission('tenants.read') || hasPermission('tenants.manage'))
    && (candidate.filter === undefined || ['all', 'attention', 'active', 'setup'].includes(String(candidate.filter)))) {
    return { kind: 'view', view: { type: 'view', view: candidate.view as WorkspaceRequest['view'], title: typeof candidate.title === 'string' ? candidate.title.slice(0, 120) : '', filter: (candidate.filter || 'all') as WorkspaceFilter } };
  }
  return null;
}

export function selectWorkspaceTenants(tenants: readonly Tenant[], filter: WorkspaceFilter = 'all'): Tenant[] {
  if (filter === 'attention') return getAttentionItems(tenants).map(item => item.tenant);
  if (filter === 'setup') return getSetupTenants(tenants);
  if (filter === 'active') return tenants.filter(tenant => tenant.status === 'active');
  return [...tenants];
}

export function fleetSummary(tenants: readonly Tenant[]) {
  const health = { healthy: 0, degraded: 0, down: 0, unknown: 0 };
  for (const tenant of tenants) {
    const status = tenant.health_status;
    if (status === 'healthy' || status === 'degraded' || status === 'down') health[status] += 1;
    else health.unknown += 1;
  }
  return { total: tenants.length, active: tenants.filter(tenant => tenant.status === 'active').length, setup: getSetupTenants(tenants).length, attention: getAttentionItems(tenants).length, health };
}
